import {
  LogOut,
  Settings,
  User,
  RotateCcw,
  X,
} from "lucide-react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "@/features/auth"
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher"
import { ThemeSwitcher } from "@/components/ThemeSwitcher"

import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const SHORT_LABELS: Record<string, string> = {
  Dashboard: 'Início',
  Contratos: 'Contratos',
  Agendamentos: 'Agenda',
  Clientes: 'Clientes',
  Grupos: 'Grupos',
  Bens: 'Bens',
  Times: 'Times',
  Comissões: 'Comissões',
  Propostas: 'Propostas',
  Configurações: 'Config.',
  Relatórios: 'Relatórios',
  'Pós-Venda': 'Pós-Venda',
  'Fluxo de Caixa': 'Caixa',
  Vendas: 'Leads',
  Pipeline: 'Leads',
}

function getShortLabel(title: string): string {
  return SHORT_LABELS[title] || title.split(' ')[0]
}

const KommoNavItem = ({
  item,
  isMobile,
  setOpenMobile,
  isActive
}: {
  item: { title: string; url: string; icon: React.ComponentType<{ className?: string }>; badge?: string | null };
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isActive: boolean;
}) => {
  const label = getShortLabel(item.title)

  return (
    <SidebarMenuItem>
      <NavLink
        to={item.url}
        end={item.url === "/"}
        onClick={() => { if (isMobile) setOpenMobile(false) }}
        className={`relative flex w-full transition-all ${
          isMobile
            ? `flex-row items-center gap-3 px-3 py-2.5 rounded-lg ${isActive ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`
            : `flex-col items-center justify-center gap-2 py-3 px-0.5 rounded-lg min-h-[72px] ${isActive ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'}`
        }`}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-[#FF3737] rounded-r-full" />
        )}
        <item.icon className={`shrink-0 ${isMobile ? 'h-5 w-5' : 'h-[22px] w-[22px]'} ${isActive ? 'text-[#FF3737]' : ''}`} />
        <span
          className={`font-medium leading-[1.1] text-center ${
            isMobile
              ? 'text-sm flex-1'
              : `text-[10px] max-w-[60px] line-clamp-2 ${isActive ? 'text-[#FF3737]' : 'text-gray-400'}`
          }`}
        >
          {isMobile ? item.title : label}
        </span>
      </NavLink>
    </SidebarMenuItem>
  )
}

const ProfileSwitcher = ({ collapsed, isSuperAdmin, isSimulating, selectedProfile, profileOptions, switchProfile, resetToRealProfile, currentProfileInfo, getDisplayLabel }: {
  collapsed: boolean;
  isSuperAdmin: boolean;
  isSimulating: boolean;
  selectedProfile: string;
  profileOptions: Array<{ value: string; label: string; description: string; icon: React.ComponentType<{ className?: string }> }>;
  switchProfile: (profile: string) => void;
  resetToRealProfile: () => void;
  currentProfileInfo: any;
  getDisplayLabel: (profile: any, isCollapsed?: boolean) => string;
}) => {
  if (!isSuperAdmin) return null;

  return (
    <div className="space-y-2 px-2">
      <Select value={selectedProfile} onValueChange={switchProfile}>
        <SelectTrigger className="h-8 bg-white/10 border-white/10 text-white text-xs rounded-lg">
          <SelectValue>
            <div className="flex items-center gap-1.5">
              {currentProfileInfo?.icon && <currentProfileInfo.icon className="h-3 w-3" />}
              {!collapsed && <span className="truncate">{getDisplayLabel(currentProfileInfo, false)}</span>}
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {profileOptions.map((profile) => (
            <SelectItem key={profile.value} value={profile.value}>
              <div className="flex items-center gap-2">
                <profile.icon className="h-4 w-4" />
                <span>{profile.label}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isSimulating && !collapsed && (
        <Button variant="outline" size="sm" onClick={resetToRealProfile} className="w-full h-7 text-xs">
          <RotateCcw className="h-3 w-3 mr-1" />
          Perfil real
        </Button>
      )}
    </div>
  );
};

interface AppSidebarProps {
  /** Sessão ainda sendo verificada: mostra um esqueleto neutro em vez do menu real. */
  loading?: boolean;
}

export function AppSidebar({ loading = false }: AppSidebarProps) {
  const { isMobile, setOpenMobile } = useSidebar()
  const { state: { user }, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const {
    selectedProfile,
    profileOptions,
    isSuperAdmin,
    isSimulating,
    switchProfile,
    resetToRealProfile,
    getCurrentNavigation,
    getCurrentProfileInfo,
    getDisplayLabel
  } = useProfileSwitcher()

  const navigation = getCurrentNavigation
  const currentProfileInfo = getCurrentProfileInfo

  const userName = user?.first_name
    ? `${user.first_name} ${user.last_name || ''}`
    : user?.email?.split('@')[0] || 'Usuário'

  const allNavItems = [
    ...navigation.primary,
    ...navigation.secondary,
  ]

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-[#1a1a1a] bg-[#0d0d0f] text-gray-300 [&_[data-sidebar=sidebar]]:bg-[#0d0d0f] [&_[data-sidebar=sidebar]]:w-[72px]"
    >
      <SidebarHeader className={`${isMobile ? "p-4 pt-6 gap-3" : "p-4 h-[11%]"} border-b border-white/20 flex items-center justify-center`}>
        <div className={`flex ${isMobile ? "justify-between items-start" : "justify-center"} w-full`}>
          <img
            src="/uploads/simbolo-volper-branca.svg"
            alt="Logo"
            className={`object-contain ${isMobile ? "h-8" : "h-10"}`}
          />
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpenMobile(false)}
              className="h-8 w-8 text-gray-400"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>

        {isMobile && (
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-2">
              {isMobile && (
                <div className="text-center">
                  <p className="text-sm font-semibold text-white">{userName}</p>
                  {currentProfileInfo?.label && (
                    <p className="text-xs text-gray-400">
                      {currentProfileInfo.label}
                      {isSimulating && " (simulando)"}
                    </p>
                  )}
                </div>
              )}
            </div>

            {isMobile && isSuperAdmin && (
              <div className="w-full">
                <ProfileSwitcher
                  collapsed={false}
                  isSuperAdmin={isSuperAdmin}
                  isSimulating={isSimulating}
                  selectedProfile={selectedProfile}
                  profileOptions={profileOptions}
                  switchProfile={switchProfile}
                  resetToRealProfile={resetToRealProfile}
                  currentProfileInfo={currentProfileInfo}
                  getDisplayLabel={getDisplayLabel}
                />
              </div>
            )}
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className={`${isMobile ? "p-4" : "px-1 py-2"} flex-1`}>
        {loading ? (
          <div className="space-y-2 px-1 py-1" aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="mx-auto h-10 w-10 animate-pulse rounded-lg bg-white/5"
              />
            ))}
          </div>
        ) : (
          <SidebarMenu className="space-y-0.5">
            {allNavItems.map((item) => {
              const isActive = item.url === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.url);
              return (
                <KommoNavItem
                  key={item.title}
                  item={item}
                  isMobile={isMobile}
                  setOpenMobile={setOpenMobile}
                  isActive={isActive}
                />
              );
            })}
          </SidebarMenu>
        )}
      </SidebarContent>

      <SidebarFooter className={`${isMobile ? "p-4" : "px-1 py-2"} border-t border-white/5`}>
        <div className="flex flex-col items-center gap-2">
          {!isMobile && loading && (
            <div className="h-9 w-9 animate-pulse rounded-full bg-white/5" aria-hidden />
          )}
          {!isMobile && !loading && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#FF3737]">
                  <Avatar className="h-9 w-9 border-2 border-white/20">
                    {user?.avatar && (
                      <AvatarImage src={user.avatar} alt={userName} className="object-cover" />
                    )}
                    <AvatarFallback className="bg-[#FF3737] text-white text-xs font-bold">
                      {userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" className="w-52 mb-2">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-muted-foreground">
                    {currentProfileInfo?.label}
                    {isSimulating && " (simulando)"}
                  </p>
                </div>
                <DropdownMenuSeparator />
                {isSuperAdmin && (
                  <>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <User className="h-4 w-4 mr-2" />
                        Trocar perfil
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {profileOptions.map((profile) => (
                          <DropdownMenuItem
                            key={profile.value}
                            onClick={() => switchProfile(profile.value)}
                            className={profile.value === selectedProfile ? "bg-accent" : ""}
                          >
                            <profile.icon className="h-4 w-4 mr-2" />
                            {profile.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    {isSimulating && (
                      <DropdownMenuItem onClick={resetToRealProfile}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Voltar ao perfil real
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate('/configuracoes')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Configurações
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {isMobile && !loading && (
            <div className="w-full space-y-0.5">
              <button
                type="button"
                onClick={() => {
                  setOpenMobile(false)
                  navigate('/configuracoes')
                }}
                className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Settings className="h-5 w-5 shrink-0" />
                Configurações
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenMobile(false)
                  logout()
                }}
                className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="h-5 w-5 shrink-0" />
                Sair
              </button>
            </div>
          )}

          <div className="flex flex-col items-center gap-1 py-1">
            <ThemeSwitcher />
            {!isMobile && (
              <span className="text-[9px] text-gray-500 font-medium">Tema</span>
            )}
          </div>
          {!isMobile && (
            <button
              type="button"
              onClick={logout}
              className="flex flex-col items-center gap-1 py-2 px-0.5 rounded-lg w-full text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
            >
              <LogOut className="h-[18px] w-[18px]" />
              <span className="text-[10px] font-medium">Sair</span>
            </button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
